import { Context, FormContext } from "../..";
import React, { useEffect, useState } from "react";

import { getCurrentAuthenticatedUser } from "../../../cognito-auth-provider/auth-provider";

enum SessionState {
    signedOut = "signed-out",
    signedIn = "signed-in",
    expired = "expired"
}

/**
 * Form updates
 */
const createUpdateEvent = (form: FormContext, name: string) => (
    session: iSessionMonitor
) => {
    form.setValue(name, session);
};

/**
 * Timer Events
 */
const createSessionEvents = (form: FormContext, name: string) => ({
    onStart: createUpdateEvent(form, name),
    onCancel: createUpdateEvent(form, name),
    onTimeout: (session: iSessionMonitor) => {
        form.setValue(
            name,
            session.status === SessionState.expired ? "" : session
        );
    }
});

interface iSessionMonitor {
    sessionId: string;
    interval?: NodeJS.Timeout;
    status: SessionState;

    start(): void;
    cancel(): void;
    timeout(): void;
}

interface iSessionMonitorEvents {
    onStart: (session: iSessionMonitor) => void;
    onCancel: (session: iSessionMonitor) => void;
    onTimeout: (session: iSessionMonitor) => void;
}

type SessionFieldProps = {
    value: iSessionMonitor;
    name: string;
    sessionName: string;
    watch: string;
};

export class Session implements iSessionMonitor {
    sessionId: string;
    interval?: NodeJS.Timeout;
    status = SessionState.signedOut;
    _events: iSessionMonitorEvents;
    _duration = 0;

    constructor(sessionId: string, callbacks: iSessionMonitorEvents) {
        this.sessionId = sessionId;
        this._events = callbacks;
    }

    start() {
        if (!this.interval) {
            this.interval = setTimeout(this.timeout.bind(this), this._duration);
            this._events.onStart(this);
        }
        return this.interval;
    }

    cancel() {
        if (this.interval) {
            clearTimeout(this.interval);
            this.interval = undefined;
            this._events.onCancel(this);
            console.info(`Session timeout cleared`);
        }
    }

    async timeout() {
        console.info(`Checking session timeout`);
        this.cancel();
        try {
            const response = await fetch(
                `${process.env.REACT_APP_EXTENDED_SESSION}?id=${this.sessionId}`
            );
            const body = await response.json();
            const remaining = body.expires - Math.floor(Date.now() / 1000);
            this._duration = Math.max((remaining - 60) * 1000, 1000);
            this.status = SessionState.signedIn;
            if (response.status === 200 && this._duration > 1000) {
                console.info(`Session expires in ${remaining} seconds`);
                console.info(`Extend session in ${remaining - 60} seconds`);
                this.start();
            } else {
                throw new Error("Extend session rejected");
            }
        } catch (error) {
            this.status = SessionState.expired;
            console.warn(error);
        }
        this._events.onTimeout(this);
    }
}

const initEffect = (
    form: FormContext,
    name: string,
    props: SessionFieldProps,
    loading: boolean,
    setLoading: (loading: boolean) => void
) => () => {
    const getUser = async () => {
        const user = await getCurrentAuthenticatedUser();
        // console.log("get user", user);
        try {
            const { username } = user;
            const session = new Session(
                username,
                createSessionEvents(form, name)
            );
            session.start();
            form.setValue(name, session);
        } catch (error) {
            if (error instanceof TypeError) {
                console.info("User not signed in");
            } else {
                console.log(error);
            }
            form.setValue(name, "");
        }
        setLoading(false);
    };
    if (!props.value && !loading) {
        setLoading(true);
        getUser();
    }
};

/**
 * Session Field
 * @param props
 * @returns
 */
const SessionField = (props: SessionFieldProps) => {
    const form = React.useContext(Context) as FormContext;
    const name = props.sessionName || props.name;
    const watch = (props.watch || "")
        .split(",")
        .map(v => form.getValue(v.trim()));
    const [loading, setLoading] = useState(false);

    useEffect(initEffect(form, name, props, loading, setLoading), watch);

    return <div style={{display: "none"}}>User session: {props.value && props.value.status}</div>;
};

export default SessionField;
